import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../components/common/IconWrapper';
import { IconType } from 'react-icons';
import {
  HiEnvelope,
  HiPhone,
  HiMapPin,
  HiSparkles,
  HiArrowRight,
  HiChatBubbleBottomCenter,
  HiClock,
  HiShieldCheck,
  HiGlobeAlt,
  HiUsers,
  HiRocketLaunch,
  HiLightBulb
} from 'react-icons/hi2';
import { ContactForm } from '../components/contact/ContactForm';
import { OfficeLocations } from '../components/contact/OfficeLocations';

interface ContactMethod {
  title: string;
  description: string;
  icon: IconType;
  action: string;
  link: string;
}

interface Highlight {
  title: string;
  description: string;
  icon: IconType;
  accent: string;
}

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  result: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const contactMethods: ContactMethod[] = [
  {
    title: 'Email',
    description: 'Send us an email and we\'ll get back to you within 24 hours.',
    icon: HiEnvelope,
    action: 'Send Email',
    link: 'mailto:info@firstlogic.in'
  },
  {
    title: 'Phone',
    description: 'Call us directly to speak with a team member.',
    icon: HiPhone,
    action: 'Call Now',
    link: 'tel:+917649099000'
  },
  {
    title: 'Office',
    description: 'Visit us at our office locations worldwide.',
    icon: HiMapPin,
    action: 'View Locations',
    link: '#locations'
  }
];

const contactHighlights: Highlight[] = [
  {
    title: 'Strategic Discovery Call',
    description: 'Get clarity on scope, budget, and the right team in 30 minutes.',
    icon: HiLightBulb,
    accent: 'from-amber-400/80 to-orange-500/80'
  },
  {
    title: 'Solution Blueprint',
    description: 'Receive a rapid roadmap outlining architecture, squad mix, and timelines.',
    icon: HiSparkles,
    accent: 'from-indigo-400/80 to-blue-500/80'
  },
  {
    title: 'Launch & Scale',
    description: 'Activate agile squads with transparent metrics and weekly demos.',
    icon: HiRocketLaunch,
    accent: 'from-emerald-400/80 to-teal-500/80'
  }
];

const clientTestimonials: Testimonial[] = [
  {
    quote: 'They embedded with our product org in days and shipped a secure data platform that saved us 38% on cloud costs.',
    name: 'Priya S.',
    role: 'VP Engineering, Fintech Unicorn',
    result: '+38% cloud savings'
  },
  {
    quote: 'FirstLogic orchestrated AI, design, and mobile squads that took our MVP to 1M users without downtime.',
    name: 'Miguel R.',
    role: 'Chief Digital Officer, Retail Enterprise',
    result: '4.9★ app store rating'
  },
  {
    quote: 'Their playbooks, observability stack, and governance rituals gave us confidence to move faster.',
    name: 'Sarah L.',
    role: 'SVP Platforms, Global Bank',
    result: '8-week go-live'
  }
];

const faqs: FAQItem[] = [
  {
    question: 'How fast can a squad start?',
    answer: 'Most pods spin up within 7–10 business days after the discovery workshop, including security onboarding.'
  },
  {
    question: 'Do you work with existing vendor ecosystems?',
    answer: 'Yes. We integrate with your preferred cloud, DevOps, and PM tools while keeping governance transparent.'
  },
  {
    question: 'What engagement models do you offer?',
    answer: 'Outcome-based, squad-based, or hybrid models with flexible ramp-up/down aligned to your roadmap.'
  },
  {
    question: 'Can you co-locate with our teams?',
    answer: 'We provide onshore leadership with nearshore/offshore pods, plus on-site embeds for critical launches.'
  }
];

const heroImage = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=60';

export const ContactPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Contact FirstLogic"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <IconWrapper icon={HiChatBubbleBottomCenter} className="w-5 h-5 text-blue-400 mr-2" />
              <span className="text-sm font-medium text-white">Human-first, engineering obsessed</span>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Let's architect the
              <span className="block text-blue-400">
                future of your platform
              </span>
            </motion.h1>

            <motion.p
              className="text-xl text-white/85 mb-8 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Whether it's data estate modernization, AI pilot-to-production, or shipping mission-critical products,
              our squads plug into your roadmap within days—not months.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.a
                href="#contact-form"
                className="group inline-flex items-center justify-center px-8 py-4 rounded-full text-white font-semibold bg-blue-600 shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.03, y: -2 }}
              >
                Start a conversation
                <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              <motion.a
                href="tel:+917649099000"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold border-2 border-white/40 text-white hover:bg-white/10 backdrop-blur-sm transition-all"
                whileHover={{ scale: 1.02, y: -2 }}
              >
                Speak to a strategist
              </motion.a>
            </motion.div>

          </div>
        </div>
      </section>




      {/* Contact Form */}
      <section id="contact-form" className="py-20 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 text-sm font-semibold text-blue-600 dark:text-blue-300 mb-4"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <IconWrapper icon={HiEnvelope} className="w-5 h-5 mr-2" />
              Start a Conversation
            </motion.div>

            <motion.h2
              className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Send Us a Message
            </motion.h2>

            <motion.p
              className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Ready to discuss your project? Fill out the form below and we'll get back to you within 24 hours.
            </motion.p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-[32px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg p-6 md:p-10">
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Office Locations */}
      <section id="locations" className="py-20 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 text-sm font-semibold text-blue-600 dark:text-blue-300 mb-4"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <IconWrapper icon={HiMapPin} className="w-5 h-5 mr-2" />
              Global Presence
            </motion.div>
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Our Office
            </motion.h2>
            <motion.p
              className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Visit us at our strategically located offices around the world to experience our services firsthand.
            </motion.p>
          </motion.div>

          <div className="space-y-8">
            <div>
              <OfficeLocations />
            </div>
            <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg">
              <iframe
                title="FirstLogic Indore Office Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3680.3412051039077!2d75.83917548885495!3d22.7155559!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd7806ff193b%3A0x88696219347ac1d3!2sKohinoor%20tower!5e0!3m2!1sen!2sus!4v1763821063010!5m2!1sen!2sus"
                className="w-full h-[400px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="p-6 text-sm text-slate-600 dark:text-slate-300">
                <p className="mb-2">Find us easily via Google Maps:</p>
                <a
                  href="https://maps.app.goo.gl/foi2ce2bbdwWbokx5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Kohinoor Tower, 218, Jawahar Marg, Opposite HDFC Bank, North Rajmohalla, Indore
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};